<template>
  <v-container
    fluid
    class="d-flex pa-0"
    style="position: relative; height: 100%"
  >
    <v-row no-gutters>
      <v-col md>
        <MglMap
          container="map-1"
          :center.sync="center"
          :zoom.sync="zoom"
          :accessToken="accessToken"
          :mapStyle="mapStyle"
          :attributionControl="false"
          hash
          @load="onMap1Load"
          @mousemove="onMapMouseMoved"
        >
          <!-- Layers -->
          <MglGeojsonLayer
            :source-id="sensors.data.id"
            :source="sensors"
            layer-id="fm-gt-sensors"
            :layer="layers.sensors"
          />
          <MglGeojsonLayer
            v-if="!flowLoading['ground_truth']"
            :source-id="gtFlow.data.id"
            :source="gtFlow"
            before="fm-gt-sensors"
            layer-id="fm-ground_truth-flow"
            :layer="layers.gtFlow"
          />
          <MglGeojsonLayer
            :source-id="origins.data.id"
            :source="origins"
            layer-id="fm-gt-origins"
            :layer="layers.origins"
          />
          <MglGeojsonLayer
            :source-id="destinations.data.id"
            :source="destinations"
            layer-id="fm-gt-destinations"
            :layer="layers.destinations"
          />

          <!-- Content -->
          <MglPopup
            :close-on-click="false"
            :close-button="false"
            :showed="!hoverPopup.showed"
            :coordinates="hoverPopup.lngLat"
          >
            <v-card flat v-show="hoverPopup.feature !== null" max-width="300">
              <template v-if="featureIsOrigin(hoverPopup.feature)">
                <v-card-title>Origin</v-card-title>
                <v-card-subtitle>{{ hoverPopup.feature.id }}</v-card-subtitle>
                <v-card-text>
                  <p class="text--secondary">
                    These are typically the ports. Trucks are expected to begin
                    their trip from here.
                  </p>
                </v-card-text>
              </template>
              <template v-if="featureIsDestination(hoverPopup.feature)">
                <v-card-title>Destination</v-card-title>
                <v-card-subtitle>{{ hoverPopup.feature.id }}</v-card-subtitle>
              </template>
              <template v-if="featureIsSensor(hoverPopup.feature)">
                <v-card-title
                  >Sensor ({{ hoverPopup.feature.type }})</v-card-title
                >
                <v-card-subtitle>{{ hoverPopup.feature.id }}</v-card-subtitle>
                <v-card-text
                  >Special equipment that senses the passage of a truck and its
                  type with some accuracy.</v-card-text
                >
              </template>
              <template v-if="featureIsLink(hoverPopup.feature)">
                <v-card-title>Road Segment</v-card-title>
                <v-card-subtitle>Link Id: {{ hoverLinkId }}</v-card-subtitle>
                <v-divider class="mx-2"></v-divider>
                <v-card-title>Actual Truck Flow</v-card-title>
                <v-card-text class="text--primary">
                  <p class="text--secondary">
                    True observed truck flow on this road segment.
                  </p>
                  <v-row dense>
                    <v-col md="4" class="font-weight-bold">Current</v-col>
                    <v-col md="4">{{ gtLinkFlowAtStep }}</v-col>
                  </v-row>
                  <v-row dense>
                    <v-col md="4" class="font-weight-bold">Total</v-col>
                    <v-col md="4">{{ gtLinkTotalFlow }}</v-col>
                  </v-row>
                </v-card-text>
              </template>
            </v-card>
          </MglPopup>

          <!-- Other -->
          <div class="algo-dropdown">
            <v-select
              label="Ground Truth"
              :items="['Ground Truth']"
              :value="'Ground Truth'"
              background-color="white"
              append-icon=""
              height="10"
              dense
              outlined
              filled
              rounded
              readonly
              disabled
              light
              solo
            ></v-select>
          </div>
        </MglMap>
      </v-col>
      <v-divider vertical light></v-divider>
      <v-col md>
        <MglMap
          container="map-2"
          :center.sync="center"
          :zoom.sync="zoom"
          :accessToken="accessToken"
          :mapStyle="mapStyle"
          hash
          @load="onMap2Load"
          @mousemove="onMapMouseMoved"
        >
          <!-- Layers -->
          <MglNavigationControl position="bottom-right" />
          <MglGeojsonLayer
            :source-id="sensors.data.id"
            :source="sensors"
            layer-id="fm-sensors"
            :layer="layers.sensors"
          />
          <MglGeojsonLayer
            v-if="!flowLoading[selectedFlow]"
            :source-id="estimatedFlow.data.id"
            :source="estimatedFlow"
            before="fm-sensors"
            layer-id="fm-estimated-flow"
            :layer="layers.estimatedFlow"
          />
          <MglGeojsonLayer
            :source-id="origins.data.id"
            :source="origins"
            layer-id="fm-origins"
            :layer="layers.origins"
          />
          <MglGeojsonLayer
            :source-id="destinations.data.id"
            :source="destinations"
            layer-id="fm-destinations"
            :layer="layers.destinations"
          />

          <!-- Content -->
          <MglPopup
            :close-on-click="false"
            :close-button="false"
            :showed="!hoverPopup.showed"
            :coordinates="hoverPopup.lngLat"
          >
            <v-card flat v-show="hoverPopup.feature !== null" max-width="300">
              <template v-if="featureIsOrigin(hoverPopup.feature)">
                <v-card-title>Origin</v-card-title>
                <v-card-subtitle>{{ hoverPopup.feature.id }}</v-card-subtitle>
                <v-card-text>
                  <p class="text--secondary">
                    These are typically the ports. Trucks are expected to begin
                    their trip from here.
                  </p>
                </v-card-text>
              </template>
              <template v-if="featureIsDestination(hoverPopup.feature)">
                <v-card-title>Destination</v-card-title>
                <v-card-subtitle>{{ hoverPopup.feature.id }}</v-card-subtitle>
              </template>
              <template v-if="featureIsSensor(hoverPopup.feature)">
                <v-card-title
                  >Sensor ({{ hoverPopup.feature.type }})</v-card-title
                >
                <v-card-subtitle>{{ hoverPopup.feature.id }}</v-card-subtitle>
                <v-card-text
                  >Special equipment that senses the passage of a truck and its
                  type with some accuracy.</v-card-text
                >
              </template>
              <template v-if="featureIsLink(hoverPopup.feature)">
                <v-card-title>Road Segment</v-card-title>
                <v-card-subtitle>Link Id: {{ hoverLinkId }}</v-card-subtitle>
                <v-divider class="mx-2"></v-divider>
                <v-card-title>Estimated Truck Flow</v-card-title>
                <v-card-text class="text--primary">
                  <p class="text--secondary">
                    The estimated truck flow on this road segment as calculated
                    by the selected algorithm.
                  </p>
                  <v-row dense>
                    <v-col md="4" class="font-weight-bold">Current</v-col>
                    <v-col md="4">{{ estimatedLinkFlowAtStep }}</v-col>
                    <v-col md="4">
                      <span
                        :class="{
                          'red--text':
                            estimatedLinkFlowAtStep - gtLinkFlowAtStep !== 0,
                          'green--text':
                            estimatedLinkTotalFlow - gtLinkTotalFlow === 0,
                          'font-weight-bold':
                            estimatedLinkTotalFlow - gtLinkTotalFlow !== 0,
                        }"
                      >
                        ({{ estimatedLinkFlowAtStep - gtLinkFlowAtStep }})
                      </span>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col md="4" class="font-weight-bold">Total</v-col>
                    <v-col md="4">{{ estimatedLinkTotalFlow }}</v-col>
                    <v-col md="4">
                      <span
                        :class="{
                          'red--text':
                            estimatedLinkTotalFlow - gtLinkTotalFlow !== 0,
                          'green--text':
                            estimatedLinkTotalFlow - gtLinkTotalFlow === 0,
                          'font-weight-bold':
                            estimatedLinkTotalFlow - gtLinkTotalFlow !== 0,
                        }"
                      >
                        ({{ estimatedLinkTotalFlow - gtLinkTotalFlow }})
                      </span>
                    </v-col>
                  </v-row>
                </v-card-text>
              </template>
            </v-card>
          </MglPopup>

          <!-- Other -->
          <div class="algo-dropdown">
            <v-select
              label="Algorithm"
              :items="algos"
              v-model="selectedFlow"
              background-color="white"
              dense
              outlined
              filled
              rounded
              light
              solo
            ></v-select>
          </div>
        </MglMap>
      </v-col>
    </v-row>
    <div class="legend">
      <v-card class="mx-auto" max-width="250" tile light>
        <v-list dense flat disabled light>
          <v-subheader>LEGEND</v-subheader>
          <v-list-item-group>
            <v-list-item dense>
              <v-list-item-icon>
                <v-icon :color="colors.origin" small>mdi-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Origin</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item dense>
              <v-list-item-icon>
                <v-icon :color="colors.destination" small>mdi-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Destination</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item dense>
              <v-list-item-icon>
                <v-icon :color="colors.sensor" small>mdi-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Sensor</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
          <v-divider></v-divider>
          <v-list-item-group>
            <v-list-item>
              <v-list-item-icon>
                <v-icon :color="colors.flow.low" small>mdi-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Low Flow</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon>
                <v-icon :color="colors.flow.medium" small>mdi-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Medium Flow</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon>
                <v-icon :color="colors.flow.high" small>mdi-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>High Flow</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-card>
    </div>
    <div class="metronome" v-if="gtFlowAtStep && estimatedFlowAtStep">
      <v-card class="mx-auto pa-0" light min-width="250">
        <v-card-text>
          <v-row class="mb-1">
            <v-col class="text-left pa-1">
              <span
                class="text-lg-h6 font-weight-light mr-2"
                v-text="timestampStrAtStep"
              />
              <v-fade-transition>
                <v-avatar
                  v-if="isPlaying"
                  color="primary"
                  class="v-avatar--metronome"
                  size="12"
                ></v-avatar>
              </v-fade-transition>
            </v-col>
            <v-col md="2" class="pa-1">
              <v-btn color="primary" dark depressed fab x-small @click="toggle">
                <v-icon large>
                  {{ isPlaying ? 'mdi-pause' : 'mdi-play' }}
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-slider
            v-model="step"
            track-color="grey"
            always-dirty
            min="0"
            :max="numSteps - 1"
            step="1"
            dense
            hide-details="true"
          >
            <template v-slot:prepend>
              <v-icon color="primary" @click="decrement"> mdi-minus</v-icon>
            </template>

            <template v-slot:append>
              <v-icon color="primary" @click="increment"> mdi-plus</v-icon>
            </template>
          </v-slider>
        </v-card-text>
      </v-card>
    </div>
    <v-overlay :absolute="absolute" :value="isLoading">
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import Vue from 'vue';

import {
  MglMap,
  MglNavigationControl,
  MglGeojsonLayer,
  MglPopup,
} from 'vue-mapbox';
import * as moment from 'moment';

import 'mapbox-gl/dist/mapbox-gl.css';
import 'vue-mapbox/dist/vue-mapbox.css';
import FreightService from '@/services/FreightService';

const ORIGIN_COLOR = '#00c5c5';
const DESTINATION_COLOR = '#7600ba';
const SENSOR_COLOR = '#0366cb';

const LOW_FLOW = '#388e00';
const MEDIUM_FLOW = '#f18300';
const HIGH_FLOW = '#ce0000';

const EMPTY_COLLECTION = {
  type: 'FeatureCollection',
  features: [],
};

export default Vue.extend({
  name: 'Flow',
  components: { MglMap, MglNavigationControl, MglGeojsonLayer, MglPopup },
  data: () => ({
    // Map
    accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN || '',
    // mapStyle: 'mapbox://styles/mapbox/streets-v11',
    mapStyle: 'mapbox://styles/mapbox/light-v10',
    center: [-118.3173033, 33.9215114],
    zoom: 11,
    colors: {
      origin: ORIGIN_COLOR,
      destination: DESTINATION_COLOR,
      sensor: SENSOR_COLOR,
      flow: {
        low: LOW_FLOW,
        medium: MEDIUM_FLOW,
        high: HIGH_FLOW,
      },
    },
    hoverPopup: {
      lngLat: [0, 0],
      showed: true,
      feature: null,
    },
    // Overlay
    absolute: true,
    // Misc.
    algos: [
      { text: 'Baseline', value: 'naive_baseline' },
      { text: 'Flow Paths', value: 'naive' },
      { text: 'Reachability', value: 'reach' },
    ],
    dataset: null,
    step: 0,
    isPlaying: false,
    interval: null,
    flowSources: {},
    flowTimelines: {},
    selectedFlow: 'naive_baseline',
    loadingDataset: false,
    flowLoading: {},
  }),

  computed: {
    origins() {
      const data = this.dataset ? this.dataset.origins : EMPTY_COLLECTION;
      return {
        type: 'geojson',
        data: {
          id: 'origins',
          ...data,
        },
      };
    },
    destinations() {
      const data = this.dataset ? this.dataset.destinations : EMPTY_COLLECTION;
      return {
        type: 'geojson',
        data: {
          id: 'destinations',
          ...data,
        },
      };
    },
    sensors() {
      const data = this.dataset ? this.dataset.sensors : EMPTY_COLLECTION;
      return {
        type: 'geojson',
        data: {
          id: 'sensors',
          ...data,
        },
      };
    },
    gtFlow() {
      const data = this.flowSources['ground_truth'] || EMPTY_COLLECTION;
      return {
        type: 'geojson',
        data: {
          id: 'ground_truth-flow',
          ...data,
        },
      };
    },
    estimatedFlow() {
      const data = this.flowSources[this.selectedFlow] || EMPTY_COLLECTION;
      return {
        type: 'geojson',
        data: {
          id: 'estimated-flow',
          ...data,
        },
      };
    },
    isLoading() {
      return (
        this.loadingDataset ||
        this.flowLoading['ground_truth'] ||
        this.flowLoading[this.selectedFlow]
      );
    },
    numSteps() {
      const spec = this.$store.getters.activeDataset;
      if (spec === null) {
        return 0;
      }

      const timestampStart = moment(spec.timestamp_start);
      const timestampEnd = moment(spec.timestamp_end);
      const diff = timestampEnd.diff(timestampStart, 'hours');
      return diff + 1;
    },
    timestampAtStep() {
      const spec = this.$store.getters.activeDataset;
      if (spec === null) {
        return null;
      }
      const timestampStart = moment(spec.timestamp_start);
      return timestampStart.add(this.step, 'hours');
    },
    timestampStrAtStep() {
      if (this.timestampAtStep == null) {
        return null;
      }
      return this.timestampAtStep.format('MM/DD/YYYY HH:mm');
    },
    gtFlowAtStep() {
      if (!this.flowTimelines['ground_truth']) {
        return null;
      }

      const timestampAtStep = this.timestampAtStep;
      const currentFlow = {};
      Object.entries(this.flowTimelines['ground_truth']).forEach((entry) => {
        const [linkId, timeline] = entry;
        if (timeline[timestampAtStep]) {
          currentFlow[linkId] = timeline[timestampAtStep];
        }
      });

      return currentFlow;
    },
    estimatedFlowAtStep() {
      if (
        !this.flowTimelines[this.selectedFlow] ||
        this.flowLoading[this.selectedFlow]
      ) {
        return null;
      }

      const timestampAtStep = this.timestampAtStep;
      const currentFlow = {};
      Object.entries(this.flowTimelines[this.selectedFlow]).forEach((entry) => {
        const [linkId, timeline] = entry;
        if (timeline[timestampAtStep]) {
          currentFlow[linkId] = timeline[timestampAtStep];
        }
      });

      return currentFlow;
    },
    layers() {
      return {
        origins: {
          type: 'circle',
          paint: {
            'circle-color': ORIGIN_COLOR,
          },
        },
        destinations: {
          type: 'circle',
          paint: {
            'circle-color': DESTINATION_COLOR,
          },
        },
        sensors: {
          type: 'circle',
          paint: {
            'circle-color': SENSOR_COLOR,
          },
        },
        gtFlow: {
          type: 'line',
          paint: {
            'line-color': [
              'step',
              [
                'number',
                [
                  'get',
                  'volume',
                  [
                    'object',
                    ['get', ['get', 'linkId'], ['literal', this.gtFlowAtStep]],
                    ['literal', {}],
                  ],
                ],
                0,
              ],
              LOW_FLOW,
              20,
              MEDIUM_FLOW,
              80,
              HIGH_FLOW,
            ],
            'line-width': 4,
          },
        },
        estimatedFlow: {
          type: 'line',
          paint: {
            'line-color': [
              'step',
              [
                'number',
                [
                  'get',
                  'volume',
                  [
                    'object',
                    [
                      'get',
                      ['get', 'linkId'],
                      ['literal', this.estimatedFlowAtStep],
                    ],
                    ['literal', {}],
                  ],
                ],
                0,
              ],
              LOW_FLOW,
              20,
              MEDIUM_FLOW,
              80,
              HIGH_FLOW,
            ],
            'line-width': 4,
          },
        },
      };
    },
    // popups
    hoverLinkId() {
      if (this.hoverPopup.feature === null || !this.hoverPopup.feature.linkId) {
        return undefined;
      }
      return this.hoverPopup.feature.linkId;
    },
    gtLinkFlowAtStep() {
      if (!this.hoverLinkId) {
        return 0;
      }
      if (!this.gtFlowAtStep[this.hoverLinkId]) {
        return 0;
      }

      return this.gtFlowAtStep[this.hoverLinkId].volume;
    },
    gtLinkTotalFlow() {
      if (!this.hoverLinkId) {
        return 0;
      }
      return this.totalFlowForTimeline(
        this.flowTimelines['ground_truth'][this.hoverLinkId]
      );
    },
    estimatedLinkFlowAtStep() {
      if (!this.hoverLinkId) {
        return 0;
      }
      if (!this.estimatedFlowAtStep[this.hoverLinkId]) {
        return 0;
      }

      return this.estimatedFlowAtStep[this.hoverLinkId].volume;
    },
    estimatedLinkTotalFlow() {
      if (!this.hoverLinkId) {
        return 0;
      }
      return this.totalFlowForTimeline(
        this.flowTimelines[this.selectedFlow][this.hoverLinkId]
      );
    },
  },

  mounted() {
    if (!this.$store.getters.hasActiveDataset) {
      if (this.$route.query.datasetId) {
        FreightService.findById(this.$route.query.datasetId.toString()).then(
          (spec) => {
            if (spec != null) {
              this.$store.commit('setActiveDataset', spec);
              this.init();
            } else {
              this.$router.push('datasets');
            }
          }
        );
      } else {
        this.$router.push('datasets');
      }
    } else {
      this.init();
      if (!this.$route.query.datasetId) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            datasetId: this.$store.getters.activeDatasetId,
          },
        });
      }
    }
  },

  methods: {
    onMap1Load({ map }) {
      this.registerMapEvents(map);
    },
    onMap2Load({ map }) {
      this.registerMapEvents(map);
    },
    registerMapEvents(map) {
      // set mapbox event listeners to update Vue component data
      map.on('move', () => {
        // set the vue instance's data.center to the results of the mapbox instance method for getting the center
        this.center = map.getCenter();
      });
      map.on('zoom', () => {
        // set the vue instance's data.zoom to the results of the mapbox instance method for getting the zoom
        this.zoom = map.getZoom();
      });
    },
    onMapMouseMoved(e) {
      const features = e.map.queryRenderedFeatures(e.mapboxEvent.point);
      const feature = features.find((feature) =>
        feature.layer.id.startsWith('fm-')
      );
      if (features.length && feature) {
        // vector-layer is the id of your layer
        this.hoverPopup.lngLat = [
          e.mapboxEvent.lngLat.lng,
          e.mapboxEvent.lngLat.lat,
        ];
        this.hoverPopup.showed = true; // show the popup
        this.hoverPopup.feature = feature.properties; // feature to display
        e.map.getCanvas().style.cursor = 'pointer'; // changes the mouse to a pointer
      } else {
        this.hoverPopup.showed = false; // hide the popup when mouse not over layer
        this.hoverPopup.feature = null;
        e.map.getCanvas().style.cursor = ''; // changes mouse back to standard
      }
    },
    featureIsSensor(feature) {
      return feature && feature.id && feature.id.startsWith('s-');
    },
    featureIsOrigin(feature) {
      return feature && feature.id && feature.id.startsWith('origin-');
    },
    featureIsDestination(feature) {
      return feature && feature.id && feature.id.startsWith('dest-');
    },
    featureIsLink(feature) {
      return feature && feature.linkId;
    },
    totalFlowForTimeline(timeline) {
      let volume = 0;
      if (timeline) {
        Object.values(timeline).forEach((item) => (volume += item.volume || 0));
      }
      return volume;
    },
    init() {
      this.loadDataset();
      this.loadFlow('ground_truth');
      this.loadFlow(this.selectedFlow);
    },
    loadDataset() {
      this.loadingDataset = true;
      return FreightService.getDataset(
        this.$store.getters.activeDatasetId || ''
      )
        .then((dataset) => (this.dataset = dataset))
        .finally(() => (this.loadingDataset = false));
    },
    loadFlow(algo) {
      if (this.flowSources && this.flowSources[algo]) {
        return Promise.resolve();
      }

      this.flowLoading = {
        ...this.flowLoading,
        [algo]: true,
      };
      return FreightService.getFlow(
        this.$store.getters.activeDatasetId || '',
        algo
      )
        .then((flow) => {
          const timelines = {};
          flow.features.forEach((feature) => {
            feature.properties.linkId = feature.properties.linkId.toString();
            const linkId = feature.properties.linkId;
            const timeline = feature.properties.timeline;
            timeline.forEach((item) => {
              item.timestamp = moment(item.timestamp);
            });

            const timelineMap = {};
            timeline.forEach((item) => {
              timelineMap[item.timestamp] = item.data;
            });

            timelines[linkId] = timelineMap;
            delete feature.properties.timeline;
          });

          this.flowSources = {
            ...this.flowSources,
            [algo]: flow,
          };
          this.flowTimelines = {
            ...this.flowTimelines,
            [algo]: timelines,
          };
        })
        .finally(() => {
          this.flowLoading = {
            ...this.flowLoading,
            [algo]: false,
          };
        });
    },
    increment() {
      this.step++;
      if (this.step >= this.numSteps) {
        this.step = this.numSteps - 1;
        if (this.isPlaying) {
          this.toggle();
        }
      }
    },
    decrement() {
      this.step--;
      if (this.step < 0) {
        this.step = 0;
      }
    },
    toggle() {
      this.isPlaying = !this.isPlaying;
      if (this.isPlaying) {
        this.interval = setInterval(this.increment, 500);
      } else if (this.interval !== null) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },
  },

  watch: {
    selectedFlow: function (newAlgo) {
      this.loadFlow(newAlgo);
    },
  },
});
</script>

<style lang="scss">
.mapboxgl-popup-content {
  padding: 0 !important;
}

.theme--dark {
  .mapboxgl-popup-anchor-top,
  .mapboxgl-popup-anchor-top-left,
  .mapboxgl-popup-anchor-top-right {
    .mapboxgl-popup-tip {
      border-bottom-color: #1e1e1e;
    }
  }

  .mapboxgl-popup-anchor-bottom,
  .mapboxgl-popup-anchor-bottom-left,
  .mapboxgl-popup-anchor-bottom-right {
    .mapboxgl-popup-tip {
      border-top-color: #1e1e1e;
    }
  }

  .mapboxgl-popup-anchor-left,
  .mapboxgl-popup-anchor-bottom-left {
    .mapboxgl-popup-tip {
      border-right-color: #1e1e1e;
    }
  }

  .mapboxgl-popup-anchor-right,
  .mapboxgl-popup-anchor-top-right {
    .mapboxgl-popup-tip {
      border-left-color: #1e1e1e;
    }
  }
}
</style>

<style lang="scss" scoped>
.algo-dropdown {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 180px;
}

.legend {
  position: absolute;
  top: 10px;
  right: 10px;
}

.metronome {
  position: absolute;
  bottom: 35px;
  left: 10px;
}

@keyframes metronome-example {
  from {
    transform: scale(0.5);
  }

  to {
    transform: scale(1);
  }
}

.v-avatar--metronome {
  animation-name: metronome-example;
  animation-iteration-count: infinite;
  animation-direction: alternate;
  animation-duration: 1s;
}

.v-divider {
  z-index: 999;
}

.v-list-item__icon:first-child {
  margin-right: 0 !important;
}
</style>
